/**
 * Thin axios wrappers around the live-session API. The frontend dev server
 * (CRA) proxies /api/* to localhost:7071 via the root package.json `proxy`
 * field, so a relative path works in both dev and production.
 *
 * The animator-only endpoints accept an optional `animatorEmail` which is
 * forwarded as `x-dev-animator-email`. In a deployed SWA, the
 * `x-ms-client-principal` header is injected by the platform and this dev
 * fallback is ignored — but in local development SWA isn't in the path, so
 * the API depends on the dev header to identify the animator.
 */

import axios from 'axios';

const BASE = '/api';

function devAuthHeaders(animatorEmail) {
  return animatorEmail ? { 'x-dev-animator-email': animatorEmail } : {};
}

export async function createSession({ quizId, animatorEmail } = {}) {
  const { data } = await axios.post(
    `${BASE}/liveCreateSession`,
    { quizId },
    { headers: devAuthHeaders(animatorEmail) }
  );
  return data;
}

export async function joinSession({ code, participantId }) {
  const body = { code };
  if (participantId) body.participantId = participantId;
  const { data } = await axios.post(`${BASE}/liveJoinSession`, body);
  return data;
}

export async function getSession({ code, participantId, knownVersion, signal }) {
  const params = { code };
  if (participantId) params.participantId = participantId;
  if (knownVersion !== undefined && knownVersion !== null) params.knownVersion = knownVersion;
  const { data } = await axios.get(`${BASE}/liveGetSession`, { params, signal });
  return data;
}

export async function submitAnswer({ code, participantId, questionIndex, optionIndex }) {
  const { data } = await axios.post(`${BASE}/liveSubmitAnswer`, {
    code,
    participantId,
    questionIndex,
    optionIndex
  });
  return data;
}

export async function getAnimatorView({ code, knownVersion, signal, animatorEmail }) {
  const params = { code };
  if (knownVersion !== undefined && knownVersion !== null) params.knownVersion = knownVersion;
  const { data } = await axios.get(`${BASE}/liveGetAnimatorView`, {
    params,
    signal,
    headers: devAuthHeaders(animatorEmail)
  });
  return data;
}

export async function animatorAction({ code, action, animatorEmail }) {
  const { data } = await axios.post(
    `${BASE}/liveAnimatorAction`,
    { code, action },
    { headers: devAuthHeaders(animatorEmail) }
  );
  return data;
}

export async function getResult({ code, participantId }) {
  const { data } = await axios.get(`${BASE}/liveGetResult`, {
    params: { code, participantId }
  });
  return data;
}
